import { useEffect, useMemo, useRef } from "react";
import { ThreadView } from "@/features/intelligence/components/ThreadView";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import { assistantNode } from "@/features/intelligence/lib/thread-state";
import type { ChatCallError } from "@/features/intelligence/api/client";
import type { ChatSurfaceApi } from "@/features/intelligence/hooks/use-chat-surface";
import { fontSizeVar } from "@/shared/ui/type-scale";
import { useChatToolCards } from "@/features/intelligence/home/chat-tool-cards";

function useChatErrorSentence(): (error: ChatCallError | null) => string | null {
  const { t } = useTranslation();
  return useMemo(() => {
    return (error: ChatCallError | null): string | null => {
      if (!error) return null;
      if (error.code === "SESSION_EXPIRED") return t("intelligence.error.sessionExpired");
      if (error.code === "TURN_IN_PROGRESS" || error.status === 409)
        return t("intelligence.error.conflict");
      if (error.status === 404) return t("intelligence.error.notFound");
      return t("intelligence.error.generic");
    };
  }, [t]);
}

export function HomeThread({ surface }: { surface: ChatSurfaceApi }) {
  const { t } = useTranslation();
  const sentence = useChatErrorSentence();
  const cards = useChatToolCards();

  const scrollRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const streamingText = assistantNode(surface.thread)?.text ?? "";

  function onThreadScroll() {
    const el = scrollRef.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 64;
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: message changes trigger scrolling; mutable ref.current values are intentionally not dependencies.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !stick.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: surface.busy ? "auto" : "smooth" });
  }, [surface.thread.nodes.length, streamingText, surface.busy]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: changing threads intentionally resets the mutable scroll-stick flag.
  useEffect(() => {
    stick.current = true;
  }, [surface.activeId]);

  const threadMessage = surface.threadError
    ? (sentence(surface.threadError) ?? t("intelligence.error.thread"))
    : null;
  const actionMessage = sentence(surface.actionError);
  const canRegenerate =
    !surface.busy &&
    surface.activeId !== null &&
    surface.thread.nodes.some((n) => n.role === "assistant");

  return (
    <div
      ref={scrollRef}
      onScroll={onThreadScroll}
      className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-[16px]"
    >
      <div className="mx-auto flex w-full max-w-[760px] flex-col gap-[20px] px-[32px] pt-[24px]">
        {surface.threadState === "loading" && surface.thread.nodes.length === 0 && (
          <p
            className="text-[var(--color-text-muted)]"
            style={{ fontFamily: "var(--font-display)", fontSize: fontSizeVar("md") }}
          >
            {t("intelligence.loadingThread")}
          </p>
        )}
        {threadMessage ? (
          <p
            className="text-[var(--color-text-muted)]"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: fontSizeVar("md"),
              lineHeight: "160%",
              letterSpacing: "-0.01em",
            }}
          >
            {threadMessage}
          </p>
        ) : (
          <ThreadView
            thread={surface.thread}
            canReconnect={surface.canReconnect}
            onReconnect={() => void surface.reconnect()}
            onDecide={(approved, reason) => void surface.answerApproval(approved, reason)}
            approvalUnconfirmed={surface.approvalOutcome === "unknown"}
            answeringApproval={surface.answeringApproval}
            canRegenerate={canRegenerate}
            onRegenerate={() => void surface.regenerate()}
            frontendCalls={surface.frontendCalls}
            belowTool={cards.below}
            toolStage={cards.stage}
          />
        )}
        {actionMessage && (
          <p
            className="text-[var(--color-text-muted)]"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: fontSizeVar("sm"),
              lineHeight: "150%",
              letterSpacing: "-0.01em",
            }}
          >
            {actionMessage}
          </p>
        )}
      </div>
    </div>
  );
}
